import { RewardType } from '@/entities/reward-track';
import type { Encounter } from '@/entities/reward-track';
import { formatScore, plural } from '@/shared/lib/format';

/**
 * Номинал награды строкой — заголовок карточки.
 *
 * Игрок приходит в Карьеру смотреть, что получит, а не как называется
 * ступень. Раньше здесь стояло `title ?? formatReward(...)`, из-за чего
 * название и номинал исключали друг друга: при заполненном `title` игрок
 * не видел награду вообще, а при пустом в заголовок падал номинал.
 * На живых данных `title` пуст почти везде, и экран выглядел разнобоем.
 *
 * Реальных вариантов, кроме Фанкоинов, в макете нет — иконка Множителя
 * не определена.
 */
export function formatReward(encounter: Encounter): string {
  switch (encounter.rewardType) {
    case RewardType.FUNCOINS: {
      const amount = Number(encounter.rewardValue);
      return `${formatScore(amount)} ${plural(amount, 'фанкоин', 'фанкоина', 'фанкоинов')}`;
    }
    case RewardType.ACTIVITY_MULTIPLIER:
      return `×${encounter.rewardValue}`;
  }
}

/**
 * Подпись под заголовком, разобранная на части.
 *
 * Целой строкой не отдаём: в макете (узел 438:51635) середина —
 * «получи 300 фанкоинов» — выделена розовым, а склеивать разметку
 * в модели нельзя.
 */
export interface EncounterSubtitle {
  /** «Собери 200 очков и ». */
  before: string;
  /** «получи 300 фанкоинов» — выделяется. */
  highlight: string;
  /** «, чтобы делать больше прогнозов». */
  after: string;
}

/**
 * Подпись под заголовком: «Собери 200 очков и получи 300 фанкоинов,
 * чтобы делать больше прогнозов».
 *
 * Собирается из данных Энкаунтера, а не хранится текстом: порог и номинал
 * приходят с сервера и правятся деплоем, и записанная руками подпись
 * разошлась бы с ними молча — как это уже было с плейсхолдерами Карьеры
 * до таблицы математики.
 *
 * Хвост «зачем это игроку» зависит от типа награды: Фанкоины тратятся на
 * Прогнозы, Множитель ускоряет Очки за активность.
 */
export function encounterSubtitle(encounter: Encounter): EncounterSubtitle {
  const threshold = encounter.pointsThreshold;
  // «очки», а не «баллы»: термин ГДД один на весь продукт, и в макете
  // Карьеры он звучал иначе, чем на соседних экранах. Решение владельца
  // от 27.08.2026 — «очки» везде
  const before = `Собери ${formatScore(threshold)} ${plural(threshold, 'очко', 'очка', 'очков')} и `;

  switch (encounter.rewardType) {
    case RewardType.FUNCOINS: {
      const amount = Number(encounter.rewardValue);
      const reward = `${formatScore(amount)} ${plural(amount, 'фанкоин', 'фанкоина', 'фанкоинов')}`;
      return {
        before,
        highlight: `получи ${reward}`,
        after: ', чтобы делать больше прогнозов',
      };
    }
    case RewardType.ACTIVITY_MULTIPLIER:
      // TODO(design): копирайта для Множителя заказчик не давал — прислан
      // только вариант с Фанкоинами. Формулировка наша, по смыслу ГДД §8.1
      return {
        before,
        highlight: `получи множитель ×${encounter.rewardValue}`,
        after: ', чтобы получать больше очков за активность',
      };
  }
}
